// The callback queue on the office's Calls screen (the call-handling parity, item 2): what each row
// says, kept out of the component so it's tested without a browser. The server decides what waits
// (calls.callbacks); this words it.
import type { CampaignRef } from '../settings/campaigns-model.ts';

/** calls.callbacks: one call still waiting on the office. */
export interface Callback {
  callId: string;
  startedAt: string;
  why: 'missed' | 'voicemail' | 'no_outcome' | 'no_reason';
  from: string;
  /** The number they dialled (older servers: absent). */
  to?: string;
  customerId: string | null;
  customerName: string | null;
  takenBy: string | null;
  textedBackAt: string | null;
  laterCall: { callId: string; startedAt: string; outcome: 'booked' | 'not_booked' } | null;
  /** The number dialled, in the company's words, when it's a tracking number with a label. */
  trackingLabel?: string | null;
  /** The campaign it's credited to. */
  campaign?: CampaignRef | null;
  /** A missed call: how long it rang, in seconds. */
  ringSeconds?: number | null;
  /** A voicemail: how long the message is, in seconds. */
  voicemailSeconds?: number | null;
  /** A voicemail's words: owner and managers only (recordings.play); absent for everyone else. */
  voicemailWords?: string | null;
}

export const WHY: Record<Callback['why'], string> = {
  missed: 'Missed',
  voicemail: 'Voicemail',
  no_outcome: 'Not recorded',
  no_reason: 'No reason yet',
};

/** How far back the tab looks: 14 days, then more on "Show older", so nothing quietly falls off. */
export const LOOK_BACK = [14, 60, 366] as const;
export function olderThan(days: number): number | null {
  const next = LOOK_BACK.find((d) => d > days);
  return next ?? null;
}

/** "Last 14 days", "Last 60 days", "Last year". */
export const lookBackWord = (days: number) => (days >= 366 ? 'Last year' : `Last ${days} days`);

/** "Called again at 3:05 p.m.: booked", given the time already worded for the company's day. */
export function laterWord(later: NonNullable<Callback['laterCall']>, when: string): string {
  return `Called again at ${when}: ${later.outcome === 'booked' ? 'booked' : 'not booked'}`;
}

/** The tab's count, as a screen reader hears it: "3 to call back". */
export const countLabel = (count: number) => `${count} to call back`;
